import { APIFetchError } from "@cur8/api-client";
import { fromAPI } from "@cur8/rich-entity";
import { skipToken, useQueries, useQuery } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import { useAPIClient } from "render/context/APIContext";

export function useRoomQueries(data: Array<string | undefined> | undefined) {
  const apiClient = useAPIClient();

  return useQueries({
    queries: data
      ? data.map((roomId) => {
          return {
            queryKey: queryKey(roomId),
            queryFn: queryFn(apiClient, roomId),
            staleTime: Infinity,
            // A 404 here means the visit is unassigned, not a transient
            // failure -- settle instantly instead of stalling callers that
            // gate on rooms settling. Other errors (500s, network blips)
            // still get a couple of retries since those can self-heal.
            retry: (count: number, error: unknown) =>
              !(
                error instanceof APIFetchError && error.response.status === 404
              ) && count < 2,
          };
        })
      : [],
  });
}

export function useRoomQuery({ roomId }: { roomId: string | undefined }) {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: queryKey(roomId),
    queryFn: roomId ? queryFn(apiClient, roomId) : skipToken,
    staleTime: Infinity,
  });
}

const queryKey = (roomId: string | undefined) => ["room", roomId];
const queryFn =
  (apiClient: APIClient, roomId: string | undefined) => async () => {
    if (roomId == null) {
      return Promise.reject("No roomId");
    }
    const roomDTO = await apiClient.schedule.fetchRoom({ roomId }).result;
    const result = fromAPI.toRoom(roomDTO);

    return result;
  };
